import React from "react";
import "./EmailPage.css";
import api from "../../apis/selectree_api";
import { toast } from "react-toastify";
import {
  FaBold,
  FaItalic,
  FaUnderline,
  FaListOl,
  FaListUl,
  FaLink,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";

const getAuthHeader = () => {
  const token = localStorage.getItem("token");
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

class EmailPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      subject: "",
      recipient: "",
      status: "",
      recipients: "all",
      users: [],
      loading: false,
      emailResult: null,
    };
    this.editorRef = React.createRef();
  }

  componentDidMount() {
    this.getAllUsers();
  }

  getAllUsers = async () => {
    this.setState({ loading: true });
    try {
      let res = await api.get(`/users/email-users`, getAuthHeader());
      console.log("All users:", res.data);
      this.setState({ users: res.data, loading: false });
    } catch (err) {
      console.log(`Error fetching users: ${err}`);
      this.setState({
        status: "Error: Failed to load users",
        loading: false,
      });
    }
  };

  getTestUsers = async () => {
    this.setState({ loading: true });
    try {
      let res = await api.get(`/users/email-test-users`, getAuthHeader());
      console.log("Test users:", res.data);
      this.setState({ loading: false });
      return res.data;
    } catch (err) {
      console.log(`Error fetching test users: ${err}`);
      this.setState({
        status: "Error: Failed to load test users",
        loading: false,
      });
      return [];
    }
  };

  handleChange = e => {
    this.setState({ [e.target.name]: e.target.value });
  };

  handleRecipientChange = e => {
    this.setState({ recipients: e.target.value });
  };

  handlePaste = e => {
    e.preventDefault();
    const clipboardData = e.clipboardData || window.clipboardData;
    const pastedText = clipboardData.getData("text/plain");
    document.execCommand("insertHTML", false, pastedText);
  };

  execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    this.editorRef.current.focus();
  };

  handleAddLink = () => {
    const url = prompt(
      "Enter the URL for the link (must start with https://):"
    );
    if (url) {
      if (/^https:\/\//i.test(url)) {
        this.execCommand("createLink", url);
      } else {
        alert("Invalid URL. Please ensure it starts with https://");
      }
    }
  };

  sendEmail = async emailData => {
    try {
      console.log("Sending email:", emailData);

      const response = await api.post(
        "/users/email",
        emailData,
        getAuthHeader()
      );
      return response.data;
    } catch (err) {
      console.log(`Error sending email: ${err}`);
      throw new Error(err.response?.data?.message || "Failed to send email");
    }
  };

  handleSubmit = async e => {
    if (e) e.preventDefault();

    this.setState({ loading: true, emailResult: null });

    try {
      // Validate required fields
      if (!this.state.subject) {
        this.setState({
          status: "Error: Subject is required",
          loading: false,
        });
        return;
      }

      // Get content from the HTML editor
      if (!this.editorRef.current || !this.editorRef.current.innerHTML.trim()) {
        this.setState({
          status: "Error: Email content is required",
          loading: false,
        });
        return;
      }

      const htmlContent = this.editorRef.current.innerHTML;

      // Validate recipient if single recipient mode
      if (this.state.recipients === "single" && !this.state.recipient) {
        this.setState({
          status: "Error: Recipient email is required",
          loading: false,
        });
        return;
      }

      let users = [];

      // Determine recipients based on selection
      if (this.state.recipients === "all") {
        users = this.state.users;
      } else if (this.state.recipients === "test") {
        users = await this.getTestUsers();
      } else {
        users = [{ email: this.state.recipient, email_notifications: true }];
      }

      if (!users || users.length === 0) {
        this.setState({
          status: "Error: No valid recipients found",
          loading: false,
        });
        return;
      }

      // Generate plain text version from HTML for fallback
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = htmlContent;
      const textContent = tempDiv.textContent || tempDiv.innerText || "";

      // Send email
      const result = await this.sendEmail({
        users: users,
        subject: this.state.subject,
        textContent: textContent,
        htmlContent: htmlContent,
      });

      // Update state with the detailed result
      this.setState({
        status: `Email operation complete`,
        emailResult: result,
        subject: "",
        recipient: "",
        loading: false,
      });

      // Reset the HTML editor
      if (this.editorRef.current) {
        this.editorRef.current.innerHTML = "";
      }

      // Show toast notification
      if (result.success) {
        toast.success(result.message || "Email sent successfully!");
      } else {
        toast.error(result.message || "Some emails failed to send");
      }

      // Don't clear the results automatically
      // We'll let the user view them and clear the form manually when ready
    } catch (err) {
      this.setState({
        status: `Error: ${err.message || "Failed to send email"}`,
        loading: false,
        emailResult: null,
      });
      toast.error("Failed to send email");
    }
  };

  clearForm = () => {
    this.setState({
      subject: "",
      recipient: "",
      emailResult: null,
      status: "",
    });

    if (this.editorRef.current) {
      this.editorRef.current.innerHTML = "";
    }
  };

  render() {
    const { subject, recipient, status, recipients, loading, emailResult } =
      this.state;

    return (
      <div className="email-container">
        <h1>Send Email</h1>

        {status && !emailResult && (
          <div className={status.includes("Error") ? "error" : "success"}>
            {status}
          </div>
        )}

        {emailResult && (
          <div className="email-result">
            <h2>Email Results</h2>
            <div className="result-summary">
              <p>
                <strong>Status: </strong>
                <span
                  className={
                    emailResult.success ? "success-text" : "error-text"
                  }
                >
                  {emailResult.success ? "Success" : "Failed"}
                </span>
              </p>
              <p>
                <strong>Message:</strong> {emailResult.message}
              </p>
              <p>
                <strong>Total Sent:</strong> {emailResult.totalSent} of{" "}
                {emailResult.details?.length || 0} emails
              </p>

              {emailResult.failures && emailResult.failures.length > 0 && (
                <p>
                  <strong>Failures:</strong> {emailResult.failures.length}{" "}
                  emails failed
                </p>
              )}
            </div>

            {emailResult.details && emailResult.details.length > 0 && (
              <div className="result-details">
                <h3>Delivery Details</h3>
                <table className="result-table">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Email</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emailResult.details.map((detail, index) => (
                      <tr key={index}>
                        <td>
                          {detail.status === "sent" ? (
                            <FaCheckCircle className="success-icon" />
                          ) : (
                            <FaTimesCircle className="error-icon" />
                          )}
                          {detail.status}
                        </td>
                        <td>{detail.email}</td>
                        <td>{detail.error || detail.reason || ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <button
              type="button"
              onClick={this.clearForm}
              className="primary-button clear-results-button"
            >
              Clear Results & Start New Email
            </button>
          </div>
        )}

        {!emailResult && (
          <form onSubmit={this.handleSubmit}>
            <div className="form-group">
              <label>Recipients:</label>
              <select
                name="recipients"
                value={recipients}
                onChange={this.handleRecipientChange}
                className="recipient-select"
              >
                <option value="all">All Users</option>
                <option value="test">Test Group</option>
                <option value="single">Single Recipient</option>
              </select>
            </div>

            {recipients === "single" && (
              <div className="form-group">
                <label>Recipient Email:</label>
                <input
                  type="email"
                  name="recipient"
                  value={recipient}
                  onChange={this.handleChange}
                  placeholder="Enter email address"
                />
              </div>
            )}

            <div className="form-group">
              <label>Subject:</label>
              <input
                type="text"
                name="subject"
                value={subject}
                onChange={this.handleChange}
                placeholder="Enter email subject"
              />
            </div>

            <div className="form-group">
              <div className="editor-toolbar">
                <button
                  type="button"
                  onClick={() => this.execCommand("bold")}
                  title="Bold"
                >
                  <FaBold />
                </button>
                <button
                  type="button"
                  onClick={() => this.execCommand("italic")}
                  title="Italic"
                >
                  <FaItalic />
                </button>
                <button
                  type="button"
                  onClick={() => this.execCommand("underline")}
                  title="Underline"
                >
                  <FaUnderline />
                </button>
                <button
                  type="button"
                  onClick={() => this.execCommand("insertOrderedList")}
                  title="Ordered List"
                >
                  <FaListOl />
                </button>
                <button
                  type="button"
                  onClick={() => this.execCommand("insertUnorderedList")}
                  title="Unordered List"
                >
                  <FaListUl />
                </button>
                <button
                  type="button"
                  onClick={() => this.handleAddLink()}
                  title="Add Link"
                >
                  <FaLink />
                </button>
              </div>
              <div
                ref={this.editorRef}
                className="rich-text-editor"
                contentEditable={true}
                suppressContentEditableWarning={true}
                onPaste={this.handlePaste}
              />
            </div>

            <div className="button-group">
              <button
                type="submit"
                disabled={loading}
                className="primary-button"
              >
                {loading ? "Sending..." : "Send Email"}
              </button>
              <button
                type="button"
                onClick={this.clearForm}
                disabled={loading}
                className="secondary-button"
              >
                Clear
              </button>
            </div>
          </form>
        )}
      </div>
    );
  }
}

export default EmailPage;
